import React, { useEffect, useMemo, useState } from 'react';
import { dealMgmt } from '../../services/dealManagementApi';
import { Badge, Card, CardHead, FilterBar } from '../../components/ui';
import { LoadingState, EmptyState, ErrorState, SaveIndicator } from '../../features/shared/PageStates';
import { formatDateTime, humanize } from '../../lib/format';

export function EmailsPage({ onNavigate, showToast }) {
  const [deals, setDeals] = useState([]);
  const [dealRef, setDealRef] = useState('');
  const [resolvedDeal, setResolvedDeal] = useState(null);
  const [emails, setEmails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [form, setForm] = useState({
    deal_ref: '',
    to: '',
    subject: '',
    body: '',
    template_id: '',
    thread_key: 'general',
    mode: 'send',
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dealMgmt.pipeline({}),
      dealMgmt.emailTemplates().catch(() => ({ templates: [] })),
    ])
      .then(([p, t]) => {
        setDeals(p.deals || []);
        setTemplates(t.templates || t.items || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const activeRef = (form.deal_ref || dealRef || '').trim();

  const dealsByRef = useMemo(() => {
    const map = new Map();
    (deals || []).forEach((d) => {
      if (d.deal_ref) map.set(String(d.deal_ref).toLowerCase(), d);
    });
    return map;
  }, [deals]);

  useEffect(() => {
    if (!activeRef) {
      setEmails([]);
      setResolvedDeal(null);
      return;
    }
    let cancelled = false;
    const fromList = dealsByRef.get(activeRef.toLowerCase());
    dealMgmt.getDeal(activeRef)
      .then((d) => {
        if (cancelled) return;
        const deal = d.deal || fromList || null;
        setResolvedDeal(deal);
        setEmails(d.emails || []);
        if (deal?.client_email) {
          setForm((p) => (p.to ? p : { ...p, to: deal.client_email }));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setResolvedDeal(fromList || null);
        setEmails([]);
        showToast?.(e.message, 'error');
      });
    return () => { cancelled = true; };
  }, [activeRef, dealsByRef, showToast]);

  const applyTemplate = (id) => {
    const tpl = templates.find(t => String(t.id) === String(id));
    if (!tpl) {
      setForm(p => ({ ...p, template_id: id }));
      return;
    }
    setForm(p => ({
      ...p,
      template_id: id,
      subject: tpl.subject || p.subject,
      body: tpl.body || tpl.content || p.body,
      thread_key: tpl.thread_key || tpl.category || p.thread_key || 'general',
    }));
  };

  const setDeal = (ref) => {
    const next = String(ref || '').trim();
    setDealRef(next);
    setForm(p => ({ ...p, deal_ref: next }));
    const deal = dealsByRef.get(next.toLowerCase());
    if (deal?.client_email) {
      setForm(p => ({ ...p, deal_ref: next, to: deal.client_email || p.to }));
    }
  };

  const send = async () => {
    const ref = (form.deal_ref || dealRef || '').trim();
    if (!ref || !form.subject || !form.body || busy) return;
    if (!form.to.trim()) {
      showToast?.('Recipient is required', 'error');
      return;
    }
    setBusy(true);
    setSaveStatus('saving');
    try {
      const res = await dealMgmt.sendEmail(ref, {
        thread_key: form.thread_key || 'general',
        recipient: form.to.trim(),
        subject: form.subject,
        body: form.body,
        workflow_step: form.mode === 'log' ? 'manual_log' : form.thread_key,
      });
      setSaveStatus('saved');
      showToast?.(
        res.message
          || (form.mode === 'log' ? 'Communication logged' : `Email sent to ${form.to.trim()}`)
      );
      setForm(p => ({ ...p, to: '', subject: '', body: '', template_id: '' }));
      const d = await dealMgmt.getDeal(ref);
      setEmails(d.emails || []);
      setResolvedDeal(d.deal || resolvedDeal);
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (e) {
      setSaveStatus('error');
      showToast?.(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingState label="Loading emails…" />;
  if (error) return <ErrorState message={error} />;

  return (
    <div>
      <FilterBar>
        <select className="field-input" value={dealRef} onChange={e => setDeal(e.target.value)} style={{ minWidth: 260 }}>
          <option value="">Select a deal…</option>
          {deals.map(d => (
            <option key={d.id} value={d.deal_ref}>{d.deal_ref} — {d.client_company_name}</option>
          ))}
        </select>
        {resolvedDeal?.id && (
          <button type="button" className="btn btn-o btn-sm" onClick={() => onNavigate('dm-deal-detail', { dmDealId: resolvedDeal.id })}>
            Open deal
          </button>
        )}
      </FilterBar>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px 0' }}>
          <CardHead title="Compose / log email" sub="Templates + deal-linked communications" />
          <SaveIndicator status={saveStatus} />
        </div>
        <div style={{ padding: '0 16px 16px' }}>
          <div className="form-row fr3">
            <div>
              <label className="field-label">Deal ref</label>
              <input
                className="field-input"
                value={form.deal_ref}
                onChange={e => {
                  const next = e.target.value;
                  setForm(p => ({ ...p, deal_ref: next }));
                  setDealRef(next.trim());
                }}
                placeholder="e.g. HC-2026-0013"
                list="email-deal-refs"
              />
              <datalist id="email-deal-refs">
                {deals.map(d => (
                  <option key={d.id} value={d.deal_ref}>{d.client_company_name}</option>
                ))}
              </datalist>
            </div>
            <div>
              <label className="field-label">Template</label>
              <select className="field-input" value={form.template_id} onChange={e => applyTemplate(e.target.value)}>
                <option value="">None</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name || t.subject || t.id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">Mode</label>
              <select className="field-input" value={form.mode} onChange={e => setForm(p => ({ ...p, mode: e.target.value }))}>
                <option value="send">Send email (SMTP)</option>
                <option value="log">Log only (do not email)</option>
              </select>
            </div>
          </div>
          <div className="form-row fr2">
            <div>
              <label className="field-label">To (recipient)</label>
              <input className="field-input" value={form.to} onChange={e => setForm(p => ({ ...p, to: e.target.value }))} />
            </div>
            <div>
              <label className="field-label">Thread key</label>
              <input className="field-input" value={form.thread_key} onChange={e => setForm(p => ({ ...p, thread_key: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="field-label">Subject</label>
            <input className="field-input" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} />
          </div>
          <div style={{ marginTop: 8 }}>
            <label className="field-label">Body</label>
            <textarea className="field-input" rows={6} value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} />
          </div>
          {templates.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
              No email templates loaded — compose freely or seed templates in Admin.
            </div>
          )}
          <button
            type="button"
            className="btn btn-p"
            style={{ marginTop: 10 }}
            disabled={busy || !activeRef || !form.subject || !form.body}
            onClick={send}
          >
            {busy ? 'Saving…' : form.mode === 'log' ? 'Log communication' : 'Send / log email'}
          </button>
        </div>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <CardHead
          title="Thread"
          sub={activeRef ? `${emails.length} message(s) · ${resolvedDeal?.deal_ref || activeRef}` : 'Select or enter a deal ref'}
        />
        {!activeRef && <EmptyState title="Select a deal" sub="Email history is stored per deal. Use the deal ref (e.g. HC-2026-0013)." />}
        {activeRef && emails.length === 0 && <EmptyState title="No emails yet" />}
        {emails.map(e => (
          <div key={e.id} style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 13 }}>{e.subject}</strong>
              <Badge type="neutral">{humanize(e.status) || 'sent'}</Badge>
              {e.direction && <Badge type="info">{e.direction}</Badge>}
              <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>
                {formatDateTime(e.created_at || e.sent_at)}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
              To: {e.recipient || e.to_address || e.to || '—'}
              {e.thread_key ? ` · ${e.thread_key}` : ''}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

export default EmailsPage;
